"use client";

import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

import { cn } from "../../lib/utils";
import { usePortalScope } from "../../lib/portal-scope";

const HoverCard = HoverCardPrimitive.Root;

const HoverCardTrigger = HoverCardPrimitive.Trigger;

/**
 * Hover card content panel rendered inside a portal.
 *
 * @param props - Accepts all `HoverCardPrimitive.Content` props including `ref`.
 * @param props.align - Horizontal alignment relative to the trigger (default `"center"`).
 * @param props.sideOffset - Distance in px from the trigger edge (default `4`).
 * @param props.className - Merged with the default hover-card styles via `cn`.
 */
function HoverCardContent({
	className,
	align = "center",
	sideOffset = 4,
	ref,
	...props
}: React.ComponentProps<typeof HoverCardPrimitive.Content>) {
	const container = usePortalScope();
	return (
		<HoverCardPrimitive.Portal container={container}>
			<HoverCardPrimitive.Content
				ref={ref}
				align={align}
				sideOffset={sideOffset}
				className={cn(
					"z-[2000] w-64 rounded-md border border-neutral-200 bg-popover p-4 text-neutral-900 shadow-md outline-none",
					"dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-50",
					"data-[state=open]:animate-in data-[state=closed]:animate-out",
					"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
					"data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
					"data-[side=bottom]:slide-in-from-top-2",
					"data-[side=left]:slide-in-from-right-2",
					"data-[side=right]:slide-in-from-left-2",
					"data-[side=top]:slide-in-from-bottom-2",
					className,
				)}
				{...props}
			/>
		</HoverCardPrimitive.Portal>
	);
}

export { HoverCard, HoverCardTrigger, HoverCardContent };
